import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Minus, Plus, Share2, ShoppingBag } from "lucide-react";
import { api, money } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import AskPriceModal from "../components/AskPriceModal";
import { Reveal } from "../components/Reveal";
import { Heart as HeartIcon } from "../components/Decor";
import Seo from "../components/Seo";

function Accordion({ title, children, testid }) {
  return (
    <details className="group bg-white rounded-2xl border border-candy-soft/70 overflow-hidden">
      <summary data-testid={testid} className="cursor-pointer list-none px-5 py-4 font-display font-semibold flex justify-between items-center hover:bg-candy-mist transition-colors duration-200">
        {title}
        <span className="text-candy font-bold group-open:rotate-45 transition-transform duration-200">+</span>
      </summary>
      <div className="px-5 pb-4 text-ink/70 font-semibold text-sm leading-relaxed">{children}</div>
    </details>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, wishlist, toggleWish } = useStore();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const [askOpen, setAskOpen] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    setProduct(null); setVariant(""); setQty(1);
    api(`/products/${slug}`).then((p) => { setProduct(p); setVariant(p.colors?.[0] || ""); }).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="text-center py-24 bg-candy-mist min-h-screen">
        <h1 className="font-display font-semibold text-3xl">This creation hopped off the hook!</h1>
        <Link to="/shop" data-testid="product-missing-back-link" className="mt-6 inline-block px-6 py-3 rounded-full bg-candy text-white font-bold">Back to shop</Link>
      </div>
    );
  }
  if (!product) return <div className="py-40 text-center text-muted font-bold bg-candy-mist min-h-screen">Unpacking the yarn…</div>;

  const wished = wishlist.includes(product.slug);
  const soldOut = product.stock <= 0;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: product.title, url }); return; } catch { /* dismissed */ }
    }
    try { await navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 2000); } catch { /* ignore */ }
  };

  return (
    <div className="bg-candy-mist min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {product && <Seo title={product.title} description={product.description} path={`/product/${product.slug}`} image={product.image} />}
        <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-muted hover:text-candy-deep transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>
        <div className="mt-6 grid md:grid-cols-2 gap-10 items-start">
          <Reveal>
            <div className="relative rounded-cloud overflow-hidden bg-gradient-to-br from-candy-soft via-pink-100 to-lilac-soft">
              <img src={product.image} alt={product.title} data-testid="product-detail-image" className="w-full aspect-square object-cover" />
              {product.badge && <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-butter-accent/90 text-ink text-xs font-extrabold">{product.badge}</span>}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 data-testid="product-detail-title" className="font-display font-semibold text-3xl sm:text-4xl">{product.title} <HeartIcon className="inline w-6 h-6 text-candy" /></h1>
            <p data-testid="product-detail-price" className="mt-2 text-2xl font-display font-semibold text-candy-deep">
              {product.quote_only ? "Ask for price" : money(product.price_cents)}
            </p>
            <p className="mt-1 text-sm font-bold" data-testid="product-detail-stock">
              {soldOut ? <span className="text-red-400">Sold out — check back soon</span>
                : product.one_of_a_kind ? <span className="text-lilac-accent">One of a kind — only {product.stock} left!</span>
                : product.stock <= 3 ? <span className="text-butter-accent" style={{ color: "#b45309" }}>Only {product.stock} left in the basket</span>
                : <span className="text-mint-accent">Ready to hook its way to you</span>}
            </p>
            <p className="mt-4 text-ink/70 font-semibold leading-relaxed">{product.description}</p>

            {product.colors?.length > 0 && (
              <div className="mt-6" data-testid="product-variant-picker">
                <p className="font-bold text-sm mb-2">Colors <span className="text-muted font-semibold">— each named, each lovable</span></p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      data-testid={`variant-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setVariant(c)}
                      aria-pressed={variant === c}
                      className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${variant === c ? "bg-candy-glow border-candy text-candy-deep" : "bg-white border-candy-soft text-ink/70 hover:bg-candy-soft"}`}
                    >
                      {variant === c && <Check className="inline w-3.5 h-3.5 mr-1" />}{c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 bg-white rounded-full px-2 py-1.5 border border-candy-soft" data-testid="product-qty-stepper">
                <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" className="w-8 h-8 rounded-full hover:bg-candy-soft flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-bold" data-testid="product-qty-value">{qty}</span>
                <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(product.stock || 1, q + 1))} aria-label="Increase quantity" className="w-8 h-8 rounded-full hover:bg-candy-soft flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              {product.quote_only ? (
                <button data-testid="ask-for-price-button" onClick={() => setAskOpen(true)} className="flex-1 min-w-[180px] py-3.5 rounded-full bg-candy hover:bg-candy-deep text-white font-bold shadow-pink transition-colors duration-200 text-center">
                  Ask for price
                </button>
              ) : (
                <button data-testid="add-to-cart-button" disabled={soldOut} onClick={() => addToCart(product.id, variant, qty)} className="flex-1 min-w-[180px] py-3.5 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-50 text-white font-bold shadow-pink transition-colors duration-200 flex items-center justify-center gap-2">
                  <ShoppingBag className="w-4 h-4" /> {soldOut ? "Sold out" : "Add to cart"}
                </button>
              )}
              <button data-testid="product-share-button" onClick={share} aria-label="Share this product" className="p-3.5 rounded-full bg-white border border-candy-soft hover:bg-candy-soft transition-colors duration-200">
                {shared ? <Check className="w-4 h-4 text-mint-accent" /> : <Share2 className="w-4 h-4 text-candy-deep" />}
              </button>
            </div>

            <div className="mt-8 space-y-3">
              <Accordion title="Yarn & Care" testid="accordion-yarn-care">
                <p><strong>Materials:</strong> {product.materials || "Owner to add — this product's materials are being documented."}</p>
                {product.care && <p className="mt-2">{product.care}</p>}
              </Accordion>
              <Accordion title="Handmade Story" testid="accordion-handmade-story">
                {product.story || "Every piece is hooked by Rachel herself — ask her team for the story behind this one!"}
              </Accordion>
              <Accordion title="Dimensions & Use" testid="accordion-dimensions-use">
                {product.measurements ? product.measurements : "Measurements for this piece are being measured up — ask us for exact dimensions."}
              </Accordion>
            </div>
          </Reveal>
        </div>

        {product.related?.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display font-semibold text-2xl text-center mb-8">More cozy favorites</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="related-products">
              {product.related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>
      <AskPriceModal product={product} open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}
